import { FetchResult } from 'src/types/TApiQueries';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { getAiBudget } from 'src/api/functions/aiSettingsApi';
import { getDestinations } from 'src/api/functions/analyticsDestinationsApi';
import { getDistinctValues } from 'src/api/functions/deliveryAnalyticsApi';
import { getSchedules } from 'src/api/functions/schedulesApi';
import { getMigrationOverview } from 'src/api/functions/migrationApi';
import { FeatureId, FeatureStatus } from './types';

/**
 * Pull a string error code out of an `ErrorResponseResult.error`, which the
 * fetch helper sets to either a bare string or the parsed JSON body
 * (e.g. `{ error: 'scheduling_disabled' }`).
 */
function errorCode(err: unknown): string | undefined {
  const raw = (err as { error?: unknown } | null | undefined)?.error;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && typeof (raw as any).error === 'string') {
    return (raw as any).error;
  }
  return undefined;
}

/**
 * Map a probe result/error onto an availability status.
 *
 * - success                       -> available
 * - 403                           -> available (route exists; perms handled elsewhere)
 * - 404                           -> unavailable (route absent on this replica)
 * - 503 / `scheduling_disabled`   -> unavailable (feature reports itself off)
 * - 5xx / network / anything else -> unknown (transient; keep last known)
 */
export function classifyAvailability(error: unknown): FeatureStatus {
  if (!error) return 'available';
  if (!isErrorResponseResult(error as any)) return 'unknown';

  const status = (error as { status?: number }).status;
  const code = errorCode(error);

  if (status === 404) return 'unavailable';
  if (status === 503 || code === 'scheduling_disabled') return 'unavailable';
  if (status === 403) return 'available';
  return 'unknown';
}

export interface FeatureProbeDef {
  id: FeatureId;
  /** Cheapest GET that proves the feature's route exists on the backend. */
  probe: (accessToken: string) => Promise<FetchResult<unknown>>;
}

export const FEATURE_PROBES: FeatureProbeDef[] = [
  { id: 'ai-usage', probe: (t) => getAiBudget(t) },
  { id: 'ai-agent', probe: (t) => getAiBudget(t) },
  { id: 'analytics-destinations', probe: (t) => getDestinations(t) },
  { id: 'delivery-analytics', probe: (t) => getDistinctValues(t, 'campaign_key') },
  { id: 'ai-schedules', probe: (t) => getSchedules(t, 'all') },
  // 503 = this deployment has no migration-onramp behind the API.
  { id: 'migration', probe: (t) => getMigrationOverview(t) },
];
